<template>
  <Navigation />

  <div class="bp-page">
    <!-- Loading state -->
    <div v-if="loadingProfile" class="bp-loading">
      <div class="bp-loading-spinner"></div>
      <span>Loading profile...</span>
    </div>

    <!-- Profile not found -->
    <div v-else-if="profileNotFound" class="bp-not-found">
      <div class="bp-not-found-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      </div>
      <h2>Profile not found</h2>
      <p>We couldn't find a business profile for <strong>{{ slug }}</strong>.</p>
    </div>

    <!-- Main profile -->
    <template v-else>
      <!-- ========== HERO BANNER ========== -->
      <div class="bp-banner" :style="bannerStyle">
        <div v-if="!bannerImageUrl" class="bp-banner-pattern"></div>
      </div>

      <!-- ========== PROFILE HEADER (overlapping banner) ========== -->
      <div class="bp-header-wrap">
        <div class="bp-header">
          <img :src="profileImage || defaultDP" class="bp-avatar" alt="Profile" />
          <div class="bp-header-info">
            <h1 class="bp-name">
              {{ business.firstName || business.businessName || 'Business Profile' }}
              <!-- <span v-if="isUserVerified"  class="verify-tick"
  title="Verified Account"
  style="margin-left:4px; display:inline-flex; align-items:center; vertical-align:middle;">
               <svg width="1em" height="1em" viewBox="0 0 24 24" style="display:block;" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#2196F3"/>
    <path d="M9.5 12.5l2 2 4-4" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="12" cy="12" r="9.2" stroke="#1976D2" stroke-width="1.6" fill="none"/>
  </svg> 
    </span> -->
      <span
                v-if="isUserVerified"
                class="verify-tick"
                title="Verified Account"
                 style="margin-left:2px; display:inline-flex; align-items:center; vertical-align:middle;"
              >
                 <svg width="26" height="26" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block;">
                   <rect x="8" y="20" width="48" height="28" rx="4" fill="#E74C3C"/>
                   <polygon points="32,38 8,20 56,20" fill="#C0392B"/>
                   <polygon points="32,38 8,48 8,20" fill="#000" opacity="0.07"/>
                   <polygon points="32,38 56,48 56,20" fill="#000" opacity="0.07"/>
                   <circle cx="32" cy="20" r="12" fill="#ECECEC"/>
                   <circle cx="32" cy="20" r="8" fill="#26A69A"/>
                   <path d="M29 20l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                 </svg>
              </span>
            
            </h1>
          
            <div class="bp-rating-row">
              <div class="bp-stars">
                <span
                  v-for="n in 5"
                  :key="'h-star-' + n"
                  class="bp-star"
                  :class="{ filled: n <= Math.round(averageRating) }"
                >&#9733;</span>
              </div>
              <span class="bp-rating-num">{{ averageRating > 0 ? averageRating : '0.0' }}</span>
              <span class="bp-rating-count">({{ totalReviews }} {{ totalReviews === 1 ? 'review' : 'reviews' }})</span>
            </div>
            <div class="bp-meta-pills">
              <span v-if="business.defaultLocation" class="bp-pill">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {{ business.defaultLocation }}
              </span>
              <a
                v-if="business.businessWebsite || business.website"
                :href="(business.businessWebsite || business.website).startsWith('http') ? (business.businessWebsite || business.website) : 'https://' + (business.businessWebsite || business.website)"
                target="_blank"
                class="bp-pill bp-pill--link"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                Website
              </a>
              <span v-if="business.preferredCountryName" class="bp-pill">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                {{ business.preferredCountryName }}
              </span>
            </div>
          </div>
          <!-- Share button -->
          <button class="bp-share-btn" @click="copyProfileLink" title="Copy profile link">
            <span class="bp-share-icon">🔗</span>
          </button>
        </div>
      </div>

      <!-- ========== TWO-COLUMN BODY ========== -->
      <div class="bp-body">
        <!-- LEFT COLUMN -->
        <div class="bp-main">

          <!-- About Card -->
          <div v-if="accountType === 'business' && business.about" class="bp-card">
            <h3 class="bp-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              About
            </h3>
            <div class="bp-about-text" v-html="sanitizedAbout"></div>
          </div>

          <!-- Services Card -->
          <div v-if="accountType === 'business' && business.servicesList && business.servicesList.length" class="bp-card">
            <h3 class="bp-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
              Services
            </h3>
            <div class="bp-services">
              <span v-for="service in business.servicesList" :key="service" class="bp-service-chip">{{ service }}</span>
            </div>
          </div>

          <!-- Rating Overview Card -->
          <div class="bp-card">
            <h3 class="bp-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              Ratings &amp; Reviews
            </h3>
            <div class="bp-rating-overview">
              <div class="bp-rating-big">
                <div class="bp-rating-number">{{ averageRating > 0 ? averageRating : '0.0' }}</div>
                <div class="bp-stars bp-stars--lg">
                  <span
                    v-for="n in 5"
                    :key="'ov-star-' + n"
                    class="bp-star lg"
                    :class="{ filled: n <= Math.round(averageRating) }"
                  >&#9733;</span>
                </div>
                <div class="bp-rating-total">{{ totalReviews }} {{ totalReviews === 1 ? 'review' : 'reviews' }}</div>
              </div>
              <div class="bp-rating-bars">
                <div v-for="star in [5, 4, 3, 2, 1]" :key="'bar-' + star" class="bp-bar-row">
                  <span class="bp-bar-label">{{ star }}</span>
                  <span class="bp-bar-star">&#9733;</span>
                  <div class="bp-bar-track">
                    <div class="bp-bar-fill" :style="{ width: totalReviews > 0 ? ((ratingBreakdown[star] / totalReviews) * 100) + '%' : '0%' }"></div>
                  </div>
                  <span class="bp-bar-count">{{ ratingBreakdown[star] || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Write a Review Card -->
          <div v-if="canRate" class="bp-card">
            <h3 class="bp-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {{ myExistingReview ? 'Update Your Review' : 'Write a Review' }}
            </h3>
            <div class="bp-review-stars">
              <span
                v-for="n in 5"
                :key="'input-star-' + n"
                class="bp-star-input"
                :class="{ filled: n <= (hoverRating || selectedRating) }"
                @mouseover="hoverRating = n"
                @mouseleave="hoverRating = 0"
                @click="selectedRating = n"
              >&#9733;</span>
              <span v-if="selectedRating" class="bp-star-label">{{ ratingLabels[selectedRating] }}</span>
            </div>
            <textarea
              v-model="reviewText"
              class="bp-review-textarea"
              :placeholder="myExistingReview ? 'Update your review...' : 'Share your experience (optional)...'"
              maxlength="500"
              rows="3"
            ></textarea>
            <div class="bp-review-footer">
              <span class="bp-char-count">{{ reviewText.length }}/500</span>
              <button
                class="bp-submit-btn"
                :disabled="!selectedRating || submittingReview"
                @click="submitReview"
              >
                {{ submittingReview ? 'Submitting...' : (myExistingReview ? 'Update Review' : 'Submit Review') }}
              </button>
            </div>
          </div>

          <!-- Login prompt -->
          <div v-else-if="!isLoggedIn" class="bp-card bp-login-prompt">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
            <p>Log in to leave a review for this profile.</p>
          </div>

          <!-- Reviews List -->
          <div class="bp-reviews-list">
            <h3 v-if="reviews.length" class="bp-reviews-heading">All Reviews ({{ totalReviews }})</h3>
            <div v-if="reviews.length === 0 && !loadingReviews" class="bp-no-reviews">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d4c8be" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <p>No reviews yet. Be the first to leave a review!</p>
            </div>
            <div v-if="loadingReviews" class="bp-reviews-loading">
              <div class="bp-loading-spinner small"></div>
              Loading reviews...
            </div>
            <div
              v-for="review in reviews"
              :key="review.reviewID"
              class="bp-review-card"
            >
              <div class="bp-review-header">
                <div class="bp-reviewer-avatar">{{ getInitials(review.reviewerName) }}</div>
                <div class="bp-reviewer-info">
                  <span class="bp-reviewer-name">{{ review.reviewerName || 'Anonymous' }}</span>
                  <span class="bp-review-date">{{ formatDate(review.createdTime) }}</span>
                </div>
                <div class="bp-stars bp-stars--sm">
                  <span
                    v-for="n in 5"
                    :key="'rev-star-' + review.reviewID + '-' + n"
                    class="bp-star sm"
                    :class="{ filled: n <= review.rating }"
                  >&#9733;</span>
                </div>
              </div>
              <p v-if="review.reviewText" class="bp-review-text">{{ review.reviewText }}</p>
              <button
                v-if="review.reviewerUserID === currentUserId"
                class="bp-delete-btn"
                @click="deleteReview(review)"
                title="Delete your review"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN — Contact sidebar -->
        <aside class="bp-sidebar">
          <div class="bp-card bp-contact-card">
            <h3 class="bp-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              Contact Info
            </h3>
            <div class="bp-contact-list">
              <div v-if="business.phoneNumber" class="bp-contact-item">
                <div class="bp-contact-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
                <div class="bp-contact-detail">
                  <span class="bp-contact-label">Phone</span>
                  <span class="bp-contact-value">{{ business.phoneNumber }}</span>
                </div>
              </div>
              <div v-if="business.Email || business.email" class="bp-contact-item">
                <div class="bp-contact-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </div>
                <div class="bp-contact-detail">
                  <span class="bp-contact-label">Email</span>
                  <span class="bp-contact-value">{{ business.Email || business.email }}</span>
                </div>
              </div>
              <div v-if="business.defaultLocation" class="bp-contact-item">
                <div class="bp-contact-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <div class="bp-contact-detail">
                  <span class="bp-contact-label">Location</span>
                  <span class="bp-contact-value">{{ business.defaultLocation }}</span>
                </div>
              </div>
              <div v-if="business.businessWebsite || business.website" class="bp-contact-item">
                <div class="bp-contact-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                </div>
                <div class="bp-contact-detail">
                  <span class="bp-contact-label">Website</span>
                  <a
                    :href="(business.businessWebsite || business.website).startsWith('http') ? (business.businessWebsite || business.website) : 'https://' + (business.businessWebsite || business.website)"
                    target="_blank"
                    class="bp-contact-link"
                  >{{ business.businessWebsite || business.website }}</a>
                </div>
              </div>
            </div>

            <!-- Social links -->
            <div v-if="Array.isArray(business.socialLinks) && business.socialLinks.length" class="bp-social-section">
              <span class="bp-contact-label">Social</span>
              <div class="bp-social-links">
                <a
                  v-for="(url, idx) in business.socialLinks"
                  :key="idx"
                  :href="url.startsWith('http') ? url : 'https://' + url"
                  target="_blank"
                  class="bp-social-chip"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  {{ url.replace(/^https?:\/\//, '').replace(/\/$/, '') }}
                </a>
              </div>
            </div>
          </div>

          <!-- Business Name card (only for business accounts with separate business name) -->
          <div v-if="accountType === 'business' && business.businessName && business.businessName !== business.firstName" class="bp-card bp-biz-card">
            <h3 class="bp-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/></svg>
              Business
            </h3>
            <p class="bp-biz-name">{{ business.businessName }}</p>
          </div>
        </aside>
      </div>
    </template>
  </div>

  <FooterSection />
</template>

<script>
import defaultDP from "../icons/Avatar.svg";
import axios from "axios";
import DOMPurify from "dompurify";
import Navigation from "./Navigation.vue";
import userDetailsServices from "../../services/userDetailsService";
import FooterSection from "../pages/footer.vue";
import { toast } from "vue-sonner";

export default {
  components: { Navigation, FooterSection },
  name: "ViewBusinessProfile",
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      resolvedUserId: null,
      loadingProfile: true,
      profileNotFound: false,
      business: {},
      profileImage: defaultDP,
      servicesList: [],
      defaultDP,
      averageRating: 0,
      totalReviews: 0,
      ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      reviews: [],
      loadingReviews: false,
      hoverRating: 0,
      selectedRating: 0,
      reviewText: "",
      submittingReview: false,
      myExistingReview: null,
      accountType: "individual",
      currentUserId: localStorage.getItem("userid") || "",
      bannerImageUrl: "",
      ratingLabels: {
        1: "Poor",
        2: "Fair",
        3: "Good",
        4: "Very Good",
        5: "Excellent",
      },
    };
  },
  computed: {
    sanitizedAbout() {
      if (!this.business.about) return "";
      return DOMPurify.sanitize(this.business.about, {
        ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "br", "p", "ul", "ol", "li"],
        ALLOWED_ATTR: ["href", "target", "rel", "class"],
      });
    },
    isLoggedIn() {
      return !!this.currentUserId;
    },
    canRate() {
      return this.isLoggedIn && this.currentUserId !== this.resolvedUserId;
    },
    bannerStyle() {
      // Priority: uploaded banner image > saved color gradient > default
      if (this.bannerImageUrl) {
        return {
          backgroundImage: `url(${this.bannerImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      }
      if (this.business.bannerColor) {
        return { background: this.business.bannerColor };
      }
      // Default gradient (warm coral)
      return {};
    },
   
                isUserVerified() {
                  const u = this.business;
                  return !!u && (
                    u.emailVerified === true || u.emailVerified == 'true' || u.emailVerified == 1 ||
                    u.phoneVerified === true || u.phoneVerified == 'true' || u.phoneVerified == 1
                  );
                },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.resolveSlugAndLoad();
  },
  methods: {
    async resolveSlugAndLoad() {
      this.loadingProfile = true;
      try {
        // Backend handles flexible slug resolution: tries slug field,
        // case-insensitive userName match, and userID in a single query
        const response = await axios.post("/api/getoneusers", { slug: this.slug });
        const user = response.data?.[0];
        if (!user?.userID) {
          this.profileNotFound = true;
          return;
        }
        this.resolvedUserId = user.userID;
        this.accountType = user.accountType === "business" ? "business" : "individual";
        userDetailsServices.getProfileImage(this.resolvedUserId).then((imgUrl) => {
          this.profileImage = imgUrl || defaultDP;
        });
        this.fetchBusinessProfile();
        this.fetchRatingSummary();
        this.fetchReviews();
      } catch (err) {
        console.error("Error resolving business profile slug:", err);
        this.profileNotFound = true;
      } finally {
        this.loadingProfile = false;
      }
    },

    fetchBusinessProfile() {
      axios
        .post("/api/userdetailslist", { userID: this.resolvedUserId })
        .then(({ data }) => {
          const user = data?.items?.[0] || {};
          this.business = {
            ...user,
            servicesList: (user.services || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          };
          this.servicesList = this.business.servicesList;
          // Fetch banner image blob if one is saved
          if (this.business.bannerImage) {
            this.fetchBannerImageBlob(this.business.bannerImage);
          }
        });
    },

    fetchRatingSummary() {
      axios
        .post("/api/ratingsummary", { targetUserID: this.resolvedUserId })
        .then(({ data }) => {
          this.averageRating = data.averageRating || 0;
          this.totalReviews = data.totalReviews || 0;
          this.ratingBreakdown = data.ratingBreakdown || {
            5: 0, 4: 0, 3: 0, 2: 0, 1: 0,
          };
        })
        .catch(() => {
          this.averageRating = this.business.rating || 0;
        });
    },

    fetchReviews() {
      this.loadingReviews = true;
      axios
        .post("/api/reviewslist?sortBy=latest&limit=50", {
          targetUserID: this.resolvedUserId,
        })
        .then(({ data }) => {
          this.reviews = data?.items || [];
          if (this.currentUserId) {
            this.myExistingReview = this.reviews.find(
              (r) => r.reviewerUserID === this.currentUserId
            );
            if (this.myExistingReview) {
              this.selectedRating = this.myExistingReview.rating;
              this.reviewText = this.myExistingReview.reviewText || "";
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching reviews:", err);
        })
        .finally(() => {
          this.loadingReviews = false;
        });
    },

    async submitReview() {
      if (!this.selectedRating) {
        toast.error("Please select a star rating");
        return;
      }
      this.submittingReview = true;
      try {
        const reviewerName = localStorage.getItem("username") || "Anonymous";
        const { data } = await axios.post("/api/addreview", {
          targetUserID: this.resolvedUserId,
          reviewerUserID: this.currentUserId,
          reviewerName,
          rating: this.selectedRating,
          reviewText: this.reviewText.trim(),
        });
        if (data.averageRating !== undefined) {
          this.averageRating = data.averageRating;
          this.totalReviews = data.totalReviews;
        }
        toast.success(
          this.myExistingReview ? "Review updated!" : "Thanks for your review!"
        );
        this.fetchReviews();
        this.fetchRatingSummary();
      } catch (err) {
        console.error("Error submitting review:", err);
        toast.error("Failed to submit review. Please try again.");
      } finally {
        this.submittingReview = false;
      }
    },

    async deleteReview(review) {
      if (!confirm("Delete your review?")) return;
      try {
        await axios.put("/api/deletereview", {
          reviewID: review.reviewID,
          reviewerUserID: this.currentUserId,
          targetUserID: this.resolvedUserId,
        });
        toast.success("Review deleted");
        this.selectedRating = 0;
        this.reviewText = "";
        this.myExistingReview = null;
        this.fetchReviews();
        this.fetchRatingSummary();
      } catch (err) {
        toast.error("Failed to delete review");
        console.error(err);
      }
    },

    async fetchBannerImageBlob(fileName) {
      if (!fileName) { this.bannerImageUrl = ""; return; }
      try {
        const res = await axios.post("/api/file/get", { fileName, albumName: "default" }, { responseType: "blob" });
        if (res.data && res.data.size > 0) {
          const contentType = res.data.type || res.headers["content-type"] || "image/jpeg";
          const blob = new Blob([res.data], { type: contentType });
          this.bannerImageUrl = URL.createObjectURL(blob);
        }
      } catch (err) {
        console.warn("Failed to load banner image:", err);
        this.bannerImageUrl = "";
      }
    },

    copyProfileLink() {
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success("Profile link copied!");
      }).catch(() => {
        toast.error("Could not copy link");
      });
    },

    getInitials(name) {
      if (!name || name === "Anonymous") return "A";
      return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
/* ================================================================
   BUSINESS PROFILE — Warm & Approachable Redesign
   ================================================================ */

/* ── Page wrapper ── */
.bp-page {
  width: 100%;
  min-height: 100vh;
  background: #faf9f7;
  font-family: 'Segoe UI', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ── Loading state ── */
.bp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
  color: #9b8e82;
  font-size: 1rem;
}
.bp-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f0ece4;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: bp-spin 0.8s linear infinite;
}
.bp-loading-spinner.small {
  width: 20px;
  height: 20px;
  border-width: 2px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}
@keyframes bp-spin {
  to { transform: rotate(360deg); }
}

/* ── Not found ── */
.bp-not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 12px;
  text-align: center;
  padding: 32px;
}
.bp-not-found h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3436;
  margin: 0;
}
.bp-not-found p {
  color: #636e72;
  margin: 0;
  font-size: 1rem;
}

/* ================================================================
   HERO BANNER
   ================================================================ */
.bp-banner {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #0d9488 0%, #0f766e 35%, #14b8a6 70%, #99f6e4 100%);
  position: relative;
  overflow: hidden;
}
.bp-banner-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 50%, rgba(255,255,255,0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255,255,255,0.06) 0%, transparent 40%),
    radial-gradient(circle at 60% 80%, rgba(255,255,255,0.04) 0%, transparent 40%);
}

/* ================================================================
   PROFILE HEADER (overlapping the banner)
   ================================================================ */
.bp-header-wrap {
  max-width: 1100px;
  margin: -60px auto 0;
  padding: 0 32px;
  position: relative;
  z-index: 2;
}
.bp-header {
  display: flex;
  align-items: flex-end;
  gap: 24px;
  padding-bottom: 28px;
  border-bottom: 1px solid #f0ece4;
}
.bp-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid #fff;
  object-fit: cover;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  flex-shrink: 0;
  background: #fff;
}
.bp-header-info {
  flex: 1;
  min-width: 0;
  padding-bottom: 4px;
}
.bp-name {
  font-size: 2rem;
  font-weight: 800;
  color: #2d3436;
  margin: 0 0 6px;
  line-height: 1.2;
  word-break: break-word;
}
.bp-rating-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.bp-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}
.bp-star {
  font-size: 20px;
  color: #c4bdb4;
  line-height: 1;
  transition: color 0.15s;
  -webkit-text-stroke: 0.5px #b0a89e;
}
.bp-star.filled {
  color: #f59e0b;
  -webkit-text-stroke: 0.5px #e8910a;
}
.bp-star.lg {
  font-size: 26px;
}
.bp-star.sm {
  font-size: 15px;
}
.bp-rating-num {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3436;
}
.bp-rating-count {
  font-size: 0.875rem;
  color: #9b8e82;
}
.bp-meta-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.bp-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid #f0ece4;
  border-radius: 20px;
  font-size: 0.82rem;
  color: #636e72;
  white-space: nowrap;
}
.bp-pill--link {
  text-decoration: none;
  color: #0d9488;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.bp-pill--link:hover {
  background: #0d9488;
  color: #fff;
  border-color: #0d9488;
}
.bp-pill--link:hover svg {
  stroke: #fff;
}
.bp-share-btn {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #374151;
  transition: all 0.2s;
  margin-bottom: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

}
.bp-share-icon {
  font-size: 18px;
  line-height: 1;
  display: block;
}

.bp-share-btn:hover {
  background: #0d9488;
  border-color: #0d9488;
  color: #fff;
}

/* ================================================================
   TWO-COLUMN BODY
   ================================================================ */
.bp-body {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 32px 48px;
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 28px;
  align-items: start;
}
.bp-main {
  min-width: 0;
}
.bp-sidebar {
  position: sticky;
  top: 90px;
}

/* ================================================================
   CARDS
   ================================================================ */
.bp-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: 24px 28px;
  margin-bottom: 20px;
  border: 1px solid rgba(240, 236, 228, 0.6);
  transition: box-shadow 0.2s;
}
.bp-card:hover {
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}
.bp-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3436;
  margin: 0 0 18px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e6f7f5;
}
.bp-card-title svg {
  color: #0d9488;
  flex-shrink: 0;
}

/* ── About Card ── */
.bp-about-text {
  font-size: 0.95rem;
  line-height: 1.75;
  color: #4a4a4a;
  word-break: break-word;
}
.bp-about-text :deep(p) {
  margin: 0 0 12px;
}
.bp-about-text :deep(p:last-child) {
  margin-bottom: 0;
}

/* ── Services Card ── */
.bp-services {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.bp-service-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  background: linear-gradient(135deg, #f0fdfa 0%, #f5fffe 100%);
  color: #0d9488;
  border: 1px solid #ccfbf1;
  border-radius: 24px;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.2s;
  cursor: default;
}
.bp-service-chip:hover {
  background: #0d9488;
  color: #fff;
  border-color: #0d9488;
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(13, 148, 136, 0.2);
}

/* ================================================================
   RATING OVERVIEW
   ================================================================ */
.bp-rating-overview {
  display: flex;
  gap: 28px;
  align-items: center;
}
.bp-rating-big {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  gap: 4px;
}
.bp-rating-number {
  font-size: 3rem;
  font-weight: 800;
  color: #2d3436;
  line-height: 1;
}
.bp-rating-total {
  font-size: 0.8rem;
  color: #9b8e82;
  margin-top: 2px;
}
.bp-rating-bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bp-bar-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.bp-bar-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #636e72;
  width: 12px;
  text-align: right;
}
.bp-bar-star {
  font-size: 0.8rem;
  color: #f59e0b;
}
.bp-bar-track {
  flex: 1;
  height: 8px;
  background: #f0ece4;
  border-radius: 4px;
  overflow: hidden;
}
.bp-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
  border-radius: 4px;
  transition: width 0.5s ease;
}
.bp-bar-count {
  font-size: 0.8rem;
  color: #9b8e82;
  width: 24px;
  text-align: right;
}

/* ================================================================
   WRITE REVIEW
   ================================================================ */
.bp-review-stars {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 14px;
}
.bp-star-input {
  font-size: 32px;
  color: #c4bdb4;
  cursor: pointer;
  transition: color 0.15s, transform 0.15s;
  line-height: 1;
}
.bp-star-input:hover {
  transform: scale(1.18);
}
.bp-star-input.filled {
  color: #f59e0b;
  text-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}
.bp-star-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #f59e0b;
  margin-left: 10px;
}
.bp-review-textarea {
  width: 100%;
  border: 1.5px solid #f0ece4;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  min-height: 70px;
  max-height: 160px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: #faf9f7;
}
.bp-review-textarea:focus {
  border-color: #0d9488;
  background: #fff;
}
.bp-review-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}
.bp-char-count {
  font-size: 0.75rem;
  color: #b0a89e;
}
.bp-submit-btn {
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 28px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.bp-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(13, 148, 136, 0.3);
}
.bp-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Login prompt ── */
.bp-login-prompt {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f0fdfa;
  border-color: #ccfbf1;
}
.bp-login-prompt p {
  margin: 0;
  font-size: 0.9rem;
  color: #636e72;
}

/* ================================================================
   REVIEWS LIST
   ================================================================ */
.bp-reviews-heading {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2d3436;
  margin: 0 0 16px;
}
.bp-no-reviews {
  text-align: center;
  color: #b0a89e;
  font-size: 0.9rem;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.bp-no-reviews p {
  margin: 0;
}
.bp-reviews-loading {
  text-align: center;
  color: #b0a89e;
  font-size: 0.9rem;
  padding: 24px 0;
}
.bp-review-card {
  background: #fff;
  border: 1px solid #f0ece4;
  border-left: 3px solid #0d9488;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 12px;
  transition: box-shadow 0.2s, transform 0.2s;
  position: relative;
}
.bp-review-card:hover {
  box-shadow: 0 3px 14px rgba(0,0,0,0.06);
  transform: translateY(-1px);
}
.bp-review-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.bp-reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
}
.bp-reviewer-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.bp-reviewer-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d3436;
}
.bp-review-date {
  font-size: 0.78rem;
  color: #b0a89e;
}
.bp-review-text {
  font-size: 0.9rem;
  color: #4a4a4a;
  line-height: 1.65;
  margin: 4px 0 0;
  word-break: break-word;
}
.bp-delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  background: none;
  border: 1px solid #f0ece4;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.75rem;
  color: #c44;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.bp-delete-btn:hover {
  background: #fff0f0;
  border-color: #c44;
}

/* ================================================================
   CONTACT SIDEBAR
   ================================================================ */
.bp-contact-card {
  border-top: 3px solid #0d9488;
}
.bp-contact-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.bp-contact-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #f5f2ee;
}
.bp-contact-item:last-child {
  border-bottom: none;
}
.bp-contact-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0fdfa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #0d9488;
}
.bp-contact-detail {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.bp-contact-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9b8e82;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}
.bp-contact-value {
  font-size: 0.9rem;
  color: #2d3436;
  word-break: break-all;
}
.bp-contact-link {
  font-size: 0.9rem;
  color: #0d9488;
  text-decoration: none;
  word-break: break-all;
  transition: color 0.2s;
}
.bp-contact-link:hover {
  color: #0f766e;
  text-decoration: underline;
}

/* Social links section */
.bp-social-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f5f2ee;
}
.bp-social-links {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.bp-social-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #faf9f7;
  border: 1px solid #f0ece4;
  border-radius: 8px;
  font-size: 0.82rem;
  color: #636e72;
  text-decoration: none;
  transition: all 0.2s;
  word-break: break-all;
}
.bp-social-chip:hover {
  background: #0d9488;
  color: #fff;
  border-color: #0d9488;
}
.bp-social-chip:hover svg {
  stroke: #fff;
}
.bp-social-chip svg {
  color: #0d9488;
  flex-shrink: 0;
}

/* Business name card */
.bp-biz-card .bp-biz-name {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3436;
  margin: 0;
}

/* ================================================================
   RESPONSIVE — TABLET
   ================================================================ */
@media (max-width: 900px) {
  .bp-body {
    grid-template-columns: 1fr;
    padding: 20px 20px 40px;
  }
  .bp-sidebar {
    position: static;
    order: -1; /* Contact info moves above reviews on mobile */
  }
  .bp-header-wrap {
    padding: 0 20px;
  }
}

/* ================================================================
   RESPONSIVE — MOBILE
   ================================================================ */
@media (max-width: 640px) {
  .bp-banner {
    height: 160px;
  }
  .bp-header-wrap {
    margin-top: -50px;
    padding: 0 16px;
  }
  .bp-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 14px;
    padding-bottom: 20px;
  }
  .bp-avatar {
    width: 100px;
    height: 100px;
  }
  .bp-name {
    font-size: 1.6rem;
  }
  .bp-rating-row {
    justify-content: center;
  }
  .bp-meta-pills {
    justify-content: center;
  }
  .bp-share-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    z-index: 3;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid #e0dcd4;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  .bp-share-btn svg {
    display: block;
    flex-shrink: 0;
  }
  .bp-header {
    position: relative;
  }
  .bp-body {
    padding: 16px 16px 32px;
    gap: 16px;
  }
  .bp-card {
    padding: 20px;
    border-radius: 14px;
  }
  .bp-rating-overview {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  .bp-rating-bars {
    width: 100%;
  }
  .bp-star-input {
    font-size: 28px;
  }
}

/* .verify-tick {
  width: 16px;
  height: 16px;
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
} */
</style>
